// FILE: src/components/business/archive/ArchiveCard.tsx
// PHOENIX PROTOCOL - ARCHIVE CARD V4.7 (RESTORED FILE INFO, NO "DETAILS" LABEL)

import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
    Calendar, FileText, Share2, 
    Eye, Download, Trash2, Loader2, FileImage, 
    FileCode, File as FileIcon, FileSpreadsheet, MessageSquare,
    Edit3, FileEdit
} from 'lucide-react';

interface ArchiveCardProps {
    title: string;
    subtitle: string;   // Document tag like "# PDF DOKUMENT"
    type: string;       // File type like "PDF"
    date: string;
    icon: React.ReactNode;
    onClick: () => void;
    onDownload?: () => void;
    onDelete?: () => void;
    onRename?: () => void;
    onEditContent?: () => void;
    onShare?: () => void;
    onAskAI?: () => void;
    isShared?: boolean;
    isFolder?: boolean;
    isLoading?: boolean;
    indexingStatus?: string;
}

export const getFileIcon = (fileType: string) => { 
    const ft = fileType ? fileType.toUpperCase() : ""; 
    if (ft === 'PDF') return <FileText className="w-5 h-5 text-danger-start" />; 
    if (['CSV', 'XLSX', 'XLS'].includes(ft)) return <FileSpreadsheet className="w-5 h-5 text-success-start" />; 
    if (['PNG', 'JPG', 'JPEG'].includes(ft)) return <FileImage className="w-5 h-5 text-primary-start" />; 
    if (['JSON', 'JS', 'TS'].includes(ft)) return <FileCode className="w-5 h-5 text-warning-start" />; 
    return <FileIcon className="w-5 h-5 text-primary-start" />; 
};

export const ArchiveCard: React.FC<ArchiveCardProps> = ({ 
    title, subtitle, type, date, icon, onClick, onDownload, onDelete, onRename, onEditContent, onShare, onAskAI,
    isShared, isFolder, isLoading, indexingStatus 
}) => {
    const { t } = useTranslation();

    return (
        <div 
            onClick={onClick} 
            className={`group relative flex flex-col justify-between h-full min-h-[12rem] sm:min-h-[14rem] p-4 sm:p-6 rounded-2xl transition-all duration-300 cursor-pointer bg-surface/40 backdrop-blur-md border border-border-main shadow-sm hover:shadow-md hover-lift`}
        >
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-start/5 to-primary-start/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            <div>
                <div className="flex flex-col mb-3 sm:mb-4 relative z-10">
                    <div className="flex justify-between items-start gap-2">
                        <div className="p-2 sm:p-2.5 rounded-xl bg-surface border border-border-main group-hover:scale-110 transition-transform duration-300">
                            {icon}
                        </div>
                        {isShared && (
                            <div className="bg-success-start/10 text-success-start p-1.5 rounded-lg border border-success-start/30 cursor-default" title={t('archive.isShared')}>
                                <Share2 size={14} />
                            </div>
                        )}
                    </div>
                    <div className="mt-3 sm:mt-4">
                        <h2 className="text-lg sm:text-xl font-bold text-text-primary line-clamp-2 leading-tight tracking-tight group-hover:text-primary-start transition-colors break-words">{title}</h2>
                        <div className="flex items-center gap-2 mt-2">
                            <Calendar className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-text-muted flex-shrink-0" />
                            <p className="text-xs font-black uppercase tracking-widest text-text-muted truncate">{date}</p>
                        </div>
                    </div>
                </div>

                {/* Restored file information section (no "DETAILS" label) */}
                <div className="flex flex-col mb-4 sm:mb-6 relative z-10">
                    <div className="space-y-1.5 pl-1">
                        {/* File type icon + name */}
                        <div className="flex items-center gap-2 text-sm sm:text-base font-medium text-text-secondary">
                            {getFileIcon(type)}
                            <span className="truncate">{type}</span>
                        </div>
                        {/* Subtitle tag (e.g., "# PDF DOKUMENT") */}
                        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-text-muted">
                            <span className="truncate">{subtitle}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative z-10 pt-3 sm:pt-4 border-t border-border-main flex items-center justify-between min-h-[2.5rem] sm:min-h-[3rem]">
                <span className="text-xs sm:text-sm font-medium text-primary-start group-hover:text-primary-start/80 transition-colors flex items-center gap-1">{isFolder ? t('archive.openFolder') : ''}</span>
                
                <div className="flex gap-1 items-center flex-wrap justify-end">
                    {!isFolder && indexingStatus === 'READY' && onAskAI && (
                         <button onClick={(e) => { e.stopPropagation(); onAskAI(); }} className="p-1.5 sm:p-2 rounded-lg text-text-muted hover:text-primary-start hover:bg-primary-start/10 transition-colors hover-lift" title="Bisedo me Dokumentin"><MessageSquare size={16} /></button>
                    )}
                    
                    {!isFolder && onShare && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onShare(); }} 
                            className={`p-1.5 sm:p-2 rounded-lg transition-colors hover-lift ${isShared ? 'bg-success-start/20 text-success-start hover:bg-success-start/30' : 'text-text-muted hover:text-text-primary hover:bg-hover'}`} 
                            title={isShared ? t('archive.unshare') : t('archive.share')}
                        >
                            <Share2 className="h-4 w-4" />
                        </button>
                    )}
                    
                    {onRename && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onRename(); }} 
                            className="p-1.5 sm:p-2 rounded-lg text-text-muted hover:text-warning-start hover:bg-warning-start/10 transition-colors hover-lift" 
                            title={t('archive.renameFile', 'Rename File')}
                        >
                            <Edit3 className="h-4 w-4" />
                        </button>
                    )}
                    
                    {onEditContent && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onEditContent(); }} 
                            className="p-1.5 sm:p-2 rounded-lg text-text-muted hover:text-primary-start hover:bg-primary-start/10 transition-colors hover-lift" 
                            title={t('archive.editData', 'Edit Document Data')}
                        >
                            <FileEdit className="h-4 w-4" />
                        </button>
                    )}
                    
                    {!isFolder && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onClick(); }} 
                            className="p-1.5 sm:p-2 rounded-lg text-text-muted hover:text-primary-start hover:bg-primary-start/10 transition-colors hover-lift" 
                            title={t('general.view')}
                        >
                            {isLoading ? <Loader2 className="h-4 w-4 animate-spin text-primary-start" /> : <Eye className="h-4 w-4" />}
                        </button>
                    )}
                    
                    {!isFolder && onDownload && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onDownload(); }} 
                            className="p-1.5 sm:p-2 rounded-lg text-text-muted hover:text-success-start hover:bg-success-start/10 transition-colors hover-lift" 
                            title={t('general.download')}
                        >
                            <Download className="h-4 w-4" />
                        </button>
                    )}
                    
                    {onDelete && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onDelete(); }} 
                            className="p-1.5 sm:p-2 rounded-lg text-text-muted hover:text-danger-start hover:bg-danger-start/10 transition-colors hover-lift" 
                            title={t('general.delete')}
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};